import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery, useQueryClient, useQueries } from '@tanstack/react-query'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { defaultDayRange, rangeToIso } from '@/shared/lib/dayRange'
import { fetchAgentBalance, fetchAgentRoots, type AgentNode } from '@/features/agent/agentApi'
import { AgentSelect } from '@/features/agent/AgentSelect'
import { SubscriberSelect } from '@/features/subscriber/SubscriberSelect'
import {
  LEDGER_ACCOUNT_TYPES,
  LEDGER_OWNER_TYPES,
  balancedAdjustLines,
  counterAccountRef,
  adjustLedger,
  fetchLedgerAccounts,
  fetchLedgerEntries,
  fetchLedgerReconciliation,
  looksLikeOwnerId,
  ownerPickerKind,
  type LedgerAccount,
  type LedgerAccountsFilter,
  type LedgerEntry,
} from '@/features/ledger/ledgerApi'
import { formatLedgerAmount } from '@/features/ledger/ledgerAmount'
import { LedgerTxnModal } from '@/features/ledger/LedgerTxnModal'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { LedgerOwnerName } from '@/features/ledger/LedgerOwnerName'
import { ReconciliationFinding } from '@/features/ledger/ReconciliationFinding'
import { looksLikeCurrencyCode } from '@/shared/lib/currencies'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { usePermissions } from '@/shared/auth/usePermissions'
import { useAuth } from '@/shared/auth/useAuth'
import { PageHeader } from '@/shared/ui/PageHeader'
import { EmptyState } from '@/shared/ui/EmptyState'
import { QueryErrorState } from '@/shared/ui/QueryErrorState'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import type { TableState } from '@/shared/ui/table/tableState'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { submitOnValid } from '@/shared/lib/submitOnValid'

const { RangePicker } = DatePicker

interface AgentLedgerRow {
  agent: AgentNode
  balance: Money | null
  available: Money | null
  debtLimit: Money | null
  loading: boolean
}

/**
 * Endpoint hali javob bermasa ANIQ shu aytiladi — "backend hali tayyor
 * emas", umumiy "xatolik" emas. Bu boshqa ro'yxatlardagi `DataTable` xato
 * holati bilan bir xil ohang: nima yo'qligi aniq, sinovchi buni panel
 * buzilgan deb qabul qilmasin.
 */
function NotReadyAlert({ error, hint }: { error: unknown; hint: string }) {
  const { t } = useTranslation()
  return (
    <Alert
      type="warning"
      showIcon
      message={t('ledger.notReadyTitle')}
      description={
        <>
          <div>{hint}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {translateError(error, t)}
          </Typography.Text>
        </>
      }
    />
  )
}


const minorAmount = (value: number | Money): number => (typeof value === 'number' ? value : value.amount)

interface AdjustFormValues {
  amount: number | Money
  reason: string
  reference?: string
}

/**
 * Qo'lda tuzatish oynasi — XAVFLI AMAL.
 *
 * ⚠️ Uchta shart BIR JOYDA majburlanadi (koordinator tuzatishi):
 *   1. Bu oynaning O'ZI tasdiqlash bosqichi — jo'natish tugmasi FAQAT
 *      shu yerdan, tasodifiy bosishning iloji yo'q
 *   2. Sabab BO'SH bo'lsa yuborilmaydi (`rules` — antd forma validatsiyasi)
 *   3. QAYTARIB BO'LMASLIGI ANIQ aytiladi (qizil `Alert`) — xato tuzatish
 *      faqat TESKARI yozuv bilan, bu oyna orqali emas
 */
function AdjustAccountModal({
  account,
  open,
  onClose,
}: {
  account: LedgerAccount
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<AdjustFormValues>()
  const [submitting, setSubmitting] = useState(false)
  // ⚠️ Qarshi hisob TENANTNIKI, ya'ni uning egasi — joriy tenant.
  // `/api/me` dagi `tenantId` `ledger.account.owner_id` bilan ayni
  // qiymat (`counterAccountRef` javadoc'i).
  const tenantId = useAuth().user?.tenant.id ?? null
  const counter = tenantId === null ? null : counterAccountRef(account, tenantId)
  const isMoney = looksLikeCurrencyCode(account.unit)

  const submit = () => {
    if (tenantId === null) return
    submitOnValid(form, async (values) => {
      setSubmitting(true)
      try {
        await adjustLedger({
          // ⚠️ Yozuv hisobga MUROJAAT bilan bog'lanadi, id bilan emas
          // (`EntryLine.account`). Server yig'indi nol bo'lishini talab
          // qiladi — shuning uchun IKKI satr: nishon hisob va qarshi
          // hisob. Bir satr yuborilganda server har safar 400 berardi
          // ("Ikki tomonlama yozuv kamida ikki qatordan iborat,
          // berilgani: 1"), ya'ni tugma umuman ishlamasdi.
          lines: balancedAdjustLines(account, tenantId, minorAmount(values.amount)),
          reason: values.reason.trim(),
          ...(values.reference?.trim() ? { reference: values.reference.trim() } : {}),
        })
        void message.success(t('ledger.adjust.done'))
        void queryClient.invalidateQueries({ queryKey: queryKeys.ledger.accounts })
        void queryClient.invalidateQueries({ queryKey: queryKeys.ledger.entries(account.id) })
        form.resetFields()
        onClose()
      } catch (error) {
        void message.error(translateError(error, t))
      } finally {
        setSubmitting(false)
      }
    })
  }

  return (
    <Modal
      open={open}
      title={t('ledger.adjust.title')}
      onCancel={() => {
        form.resetFields()
        onClose()
      }}
      onOk={submit}
      confirmLoading={submitting}
      okText={t('ledger.adjust.submit')}
      okButtonProps={{ danger: true, disabled: counter === null }}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('ledger.adjust.irreversibleTitle')}
        description={t('ledger.adjust.irreversibleText')}
      />
      <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('ledger.accounts.title')}>
          <LedgerOwnerName type={account.ownerType} id={account.ownerId} />
        </Descriptions.Item>
        <Descriptions.Item label={t('common.balance')}>
          {formatLedgerAmount(account.balance, account.unit)}
        </Descriptions.Item>
        {/*
          ⚠️ QARSHI HISOB YASHIRILMAYDI. Yozuv ikki tomonlama va pul
          qayerdan kelgani jurnalda qoladi — operator uni tugmani
          bosishdan OLDIN ko'rishi kerak. Yashirilsa, u keyin jurnaldan
          topib "bu qayerdan chiqdi?" deb so'rardi.
        */}
        <Descriptions.Item label={t('ledger.adjust.counterAccount')}>
          {counter === null ? (
            <Typography.Text type="danger">{t('ledger.adjust.counterUnknown')}</Typography.Text>
          ) : (
            <Tag>{t(`ledgerAccountType.${counter.type}`, { defaultValue: counter.type })}</Tag>
          )}
        </Descriptions.Item>
      </Descriptions>
      <Form<AdjustFormValues> form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="amount"
          label={t('ledger.adjust.amount')}
          extra={t('ledger.adjust.amountHint', { unit: account.unit })}
          rules={[
            { required: true, message: t('ledger.adjust.validationAmount') },
            {
              validator: (_, value: number | Money | null | undefined) =>
                value != null && minorAmount(value) !== 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('ledger.adjust.validationAmount'))),
            },
          ]}
        >
          {isMoney ? (
            <MoneyInput currency={account.unit} minMinor={Number.MIN_SAFE_INTEGER} placeholder="±0" />
          ) : (
            <InputNumber style={{ width: '100%' }} precision={0} placeholder="±0" />
          )}
        </Form.Item>
        <Form.Item
          name="reason"
          label={t('ledger.adjust.reason')}
          rules={[{ required: true, whitespace: true, message: t('ledger.adjust.validationReason') }]}
        >
          <Input.TextArea rows={3} maxLength={300} autoFocus />
        </Form.Item>
        <Form.Item name="reference" label={t('ledger.adjust.reference')}>
          <Input maxLength={100} placeholder={t('ledger.adjust.referencePlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

/**
 * Jadval holatidan SERVER filtrini yasaydi.
 *
 * ⚠️ FAQAT SERVER QABUL QILADIGAN maydonlar. `GET /api/ledger/accounts`
 * `ownerType`, `ownerId`, `type`, `unit`, `page`, `size` ni oladi
 * (`LedgerController#accounts`) — boshqa hech nima. Mijozda qo'shimcha
 * filtr qilish faqat JORIY SAHIFANI filtrlardi va operator uni butun
 * ro'yxat deb o'qirdi.
 */
function toAccountsFilter(state: TableState): LedgerAccountsFilter {
  const ownerType = state.filters['ownerType']?.[0]
  const type = state.filters['type']?.[0]
  const ownerId = state.filters['ownerId']?.[0] ?? ''
  return {
    ...(ownerType ? { ownerType } : {}),
    ...(type ? { type } : {}),
    ...(ownerPickerKind(ownerType) !== null && looksLikeOwnerId(ownerId) ? { ownerId } : {}),
  }
}

function AccountOwnerFilter() {
  const { t } = useTranslation()
  const { state, patch } = useTableUrlState()
  const ownerType = state.filters['ownerType']?.[0]
  const kind = ownerPickerKind(ownerType)
  const ownerId = state.filters['ownerId']?.[0] ?? null
  const previousKind = useRef(kind)

  const setOwner = useCallback(
    (value: string | null) => {
      const { ownerId: _dropped, ...rest } = state.filters
      patch({ page: 1, filters: value ? { ...rest, ownerId: [value] } : rest })
    },
    [state.filters, patch],
  )

  useEffect(() => {
    if (previousKind.current !== kind && ownerId !== null) setOwner(null)
    previousKind.current = kind
  }, [kind, ownerId, setOwner])

  if (kind === null) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12, maxWidth: 320 }}>
        {t('ledger.accounts.ownerPickHint')}
      </Typography.Text>
    )
  }
  return (
    <div style={{ width: 280 }}>
      {kind === 'AGENT' ? (
        <AgentSelect
          value={ownerId}
          onChange={setOwner}
          placeholder={t('ledgerJournal.filters.ownerAgentPlaceholder')}
        />
      ) : (
        <SubscriberSelect
          value={ownerId}
          onChange={setOwner}
          placeholder={t('ledgerJournal.filters.ownerSubscriberPlaceholder')}
        />
      )}
    </div>
  )
}

/**
 * Buxgalteriya jurnali.
 *
 * ⚠️ `ledger` shakli koordinator tomonidan TASDIQLANGAN (accounts/entries/
 * txns/adjust) — `total` YO'Q, faqat `hasNext` (`shared/api/pageResult.ts`).
 * `reconciliation` shakli hali TASDIQLANMAGAN — eng ishonchli taxmin.
 *
 * Har bo'lim MUSTAQIL so'rov: biri hali javob bermasa (404/500/tarmoq),
 * faqat o'sha bo'lim ogohlantirish ko'rsatadi, qolganlari ishlashda davom
 * etadi.
 *
 * <h2>⚠️ SAHIFALASH — SERVERDA, IKKI JADVALDA</h2>
 * Hisoblar va ko'chirma `page`/`size` bilan so'raladi va sahifa
 * o'zgarganda YANGI so'rov ketadi (`LedgerController#accounts`,
 * `#accountEntries`). Jami son SERVER BERMAYDI (`LedgerPage` javobi
 * faqat `hasNext`), shuning uchun «1–25 / N» yozuvi CHIZILMAYDI —
 * `showTotal={false}`. Taxmin qilingan sonni jami deb ko'rsatish
 * operator ishonadigan yolg'on bo'lardi.
 *
 * <h2>⚠️ SARALASH YO'Q — SERVER QO'LLAMAYDI</h2>
 * Ikkala endpointda ham `sort` parametri YO'Q. Ustunga `sorter: true`
 * qo'yish faqat JORIY SAHIFANI saralardi va bu pul jurnalida ayniqsa
 * xavfli: operator "eng katta qarzdor" deb ko'rgan qator aslida shu 25
 * qator ichidagi eng kattasi bo'lardi.
 *
 * <h2>⚠️ AGENT BALANSLARI SAHIFALANMAYDI — ENDPOINT BERMAYDI</h2>
 * `GET /api/agents/roots` da `page`/`size` yo'q va jamlangan balans
 * endpointi ham yo'q (har agent uchun alohida so'rov). Shuning uchun u
 * bo'lim `DataTable` ga o'tkazilmadi: soxta sahifalash yasashdan ko'ra
 * sahifalashsiz qolgani halol.
 */
export function LedgerPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()

  const reconciliation = useQuery({
    queryKey: queryKeys.ledger.reconciliation,
    queryFn: ({ signal }) => fetchLedgerReconciliation(signal),
  })

  const [openAccount, setOpenAccount] = useState<LedgerAccount | null>(null)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [openTxnId, setOpenTxnId] = useState<string | null>(null)
  // ⚠️ Oyna TENANT zonasida. `dayjs()` brauzernikini olardi va Kobul
  // tenantida (UTC+4:30) davr chegarasi bir kunga surilib ketardi —
  // buxgalteriya jurnalida bu jimgina noto'g'ri qoldiq degani
  // (`dayRange.ts`).
  const [entriesRange, setEntriesRange] = useState<[Dayjs, Dayjs]>(() => defaultDayRange(30))
  const { from: entriesFrom, to: entriesTo } = rangeToIso(entriesRange)

  const accountColumns = useMemo<ColumnsType<LedgerAccount>>(
    () => [
      {
        key: 'owner',
        title: t('ledger.accounts.owner'),
        width: 260,
        render: (_, row) => <LedgerOwnerName type={row.ownerType} id={row.ownerId} />,
      },
      {
        key: 'ownerType',
        title: t('ledger.accounts.ownerType'),
        width: 150,
        // ⚠️ Filtr qiymati SERVERGA ketadi. `filterMultiple: false` —
        // `@RequestParam OwnerType ownerType` YAKKA qiymat, ko'p tanlash
        // imkoni yolg'on va'da bo'lardi.
        filters: LEDGER_OWNER_TYPES.map((value) => ({
          text: t(`ledgerOwnerType.${value}`, { defaultValue: value }),
          value,
        })),
        filterMultiple: false,
        render: (_, row) => <Tag>{t(`ledgerOwnerType.${row.ownerType}`, { defaultValue: row.ownerType })}</Tag>,
      },
      {
        key: 'type',
        title: t('ledger.accounts.type'),
        width: 200,
        filters: LEDGER_ACCOUNT_TYPES.map((value) => ({
          text: t(`ledgerAccountType.${value}`, { defaultValue: value }),
          value,
        })),
        filterMultiple: false,
        render: (_, row) => t(`ledgerAccountType.${row.type}`, { defaultValue: row.type }),
      },
      {
        key: 'unit',
        title: t('ledger.accounts.unit'),
        width: 90,
        /* ⚠️ FILTR YO'Q va bu ataylab: server `unit` ni ERKIN SATR
           sifatida oladi (`@RequestParam String unit`), yopiq ro'yxati
           esa yo'q — pul kodlari ham, `BYTE` ham shu ustunda. Voronkaga
           o'ylab topilgan ro'yxat qo'yish "boshqa birlik yo'q" degan
           yolg'on xabar bo'lardi. */
        render: (_, row) => row.unit,
      },
      {
        key: 'balance',
        title: t('common.balance'),
        align: 'right',
        /*
          ⚠️ KENGLIK VA `nowrap` — pul ustuni.

          Kengliksiz antd ustunni qolgan joyga siqardi va uzun qiymat uch
          qatorga bo'linardi: "-73,193,7 / 00.45 / AFN". Bunday ko'rinishda
          raqamni o'qib bo'lmaydi va u XATO o'qilishi mumkin — bu esa pul
          ustuni.
        */
        width: 190,
        render: (_, row) => (
          <Typography.Text
            type={row.balance < 0 ? 'danger' : undefined}
            style={{ whiteSpace: 'nowrap' }}
          >
            {/* ⚠️ `0` — O'LCHANGAN NOL, bo'sh emas: server har hisobda
                `balance` beradi (`z.number()`, ixtiyoriy emas). Shuning
                uchun bu yerda hech qachon "—" chizilmaydi. */}
            {formatLedgerAmount(row.balance, row.unit)}
          </Typography.Text>
        ),
      },
      {
        key: 'debtLimit',
        title: t('agents.debtLimit'),
        align: 'right',
        width: 150,
        render: (_, row) => (
          <Typography.Text style={{ whiteSpace: 'nowrap' }}>
            {/* ⚠️ `null` = QO'YILMAGAN, nol EMAS. Ikkalasini bir xil
                ko'rsatish "limit nol, ya'ni qarz mumkin emas" degan
                teskari xulosaga olib borardi. */}
            {row.debtLimit === null
              ? t('agents.debtLimitNotSet')
              : formatLedgerAmount(row.debtLimit, row.unit)}
          </Typography.Text>
        ),
      },
      {
        key: 'createdAt',
        title: t('subscriber.createdAt'),
        width: 170,
        render: (_, row) =>
          row.createdAt === null ? (
            t('common.dash')
          ) : (
            <span title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</span>
          ),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              {
                key: 'entries',
                label: t('ledger.accounts.openEntries'),
                onClick: () => setOpenAccount(row),
              },
              {
                key: 'adjust',
                label: t('ledger.adjust.button'),
                onClick: () => {
                  setOpenAccount(row)
                  setAdjustOpen(true)
                },
                permission: 'ledger.adjust',
                danger: true,
                divider: true,
              },
            ]}
          />
        ),
      },
    ],
    [t],
  )

  // ⚠️ YUGURUVCHI QOLDIQ USTUNI YO'Q: server yozuv bilan birga qoldiqni
  // bermaydi, ko'chirma esa sahifalab keladi — bir sahifada boshlang'ich
  // qoldiq noma'lum. Uni mijozda "hisoblab" chiqarish son o'ylab topish
  // bo'lardi (`ledgerApi.ts` dagi izoh).
  const entryColumns = useMemo<ColumnsType<LedgerEntry>>(
    () => [
      {
        key: 'at',
        title: t('audit.columns.at'),
        width: 170,
        render: (_, row) => (
          <span title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</span>
        ),
      },
      {
        key: 'description',
        title: t('ledger.entries.description'),
        // Serverdagi izoh maydoni emas: tranzaksiya TURI + tashqi havola.
        // Aynan shu ikkisi "bu pul nega harakatlandi" degan savolga javob
        // beradi (`LedgerEntry` javadoc'i).
        render: (_, row) => (
          <Space size={4} wrap>
            {row.txnKind ? <Tag>{t(`txnKind.${row.txnKind}`, { defaultValue: row.txnKind })}</Tag> : null}
            {row.txnReference ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.txnReference}
              </Typography.Text>
            ) : null}
            {/* ⚠️ Ikkalasi ham bo'sh bo'lishi mumkin — u holda katak
                BO'SH qoladi, "0" yoki "yo'q" emas: yozuvda bu ma'lumot
                shunchaki YO'Q (ikkalasi ham `nullish`). */}
            {row.txnKind === null && row.txnReference === null ? t('common.dash') : null}
          </Space>
        ),
      },
      {
        key: 'amount',
        title: t('ledger.entries.amount'),
        align: 'right',
        width: 190,
        render: (_, row) => (
          <Typography.Text
            type={row.amount < 0 ? 'danger' : 'success'}
            style={{ whiteSpace: 'nowrap' }}
          >
            {row.amount >= 0 ? '+' : ''}
            {formatLedgerAmount(row.amount, row.unit)}
          </Typography.Text>
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              {
                key: 'txn',
                label: t('ledger.txn.open'),
                onClick: () => setOpenTxnId(row.txnId),
              },
            ]}
          />
        ),
      },
    ],
    [t],
  )

  // --- Agent balanslari ---
  const roots = useQuery({ queryKey: queryKeys.agents.all, queryFn: fetchAgentRoots })
  // ⚠️ `?? []` `useMemo` ICHIDA: tashqarida bo'lsa har renderda YANGI
  // massiv yaratilib, quyidagi `useMemo` hech qachon keshlanmasdi
  const allAgents = useMemo(() => roots.data ?? [], [roots.data])

  /**
   * ⚠️ YOPILGAN AGENTLAR CHIQARIB TASHLANADI — VA BU FAQAT KO'RINISH
   * MASALASI EMAS.
   *
   * Quyidagi `useQueries` HAR AGENT uchun alohida HTTP so'rov yuboradi
   * (`GET /api/agents/{id}/balance` — jamlangan endpoint yo'q). Sinov
   * muhitida bu 62 ta so'rov degani edi va ularning 59 tasi yopilgan
   * sinov agentlari uchun: sahifa ochilganda brauzer ham, gateway ham
   * keraksiz yuk olardi, jadval esa 60 qator "0 AFN" ko'rsatardi.
   *
   * Yopilgan agentda balans harakati bo'lishi mumkin emas, ya'ni bu
   * qatorlar hech qanday savolga javob bermaydi.
   */
  const agents = useMemo(() => allAgents.filter((a) => a.state !== 'CLOSED'), [allAgents])
  const hiddenAgents = allAgents.length - agents.length

  const balances = useQueries({
    queries: agents.map((agent) => ({
      queryKey: queryKeys.agents.balance(agent.id),
      queryFn: () => fetchAgentBalance(agent.id),
    })),
  })
  const agentRows: AgentLedgerRow[] = agents.map((agent, index) => {
    const query = balances[index]
    return {
      agent,
      balance: query?.data?.balance ?? null,
      available: query?.data?.available ?? null,
      debtLimit: query?.data?.debtLimit ?? null,
      loading: query?.isPending ?? true,
    }
  })
  const agentColumns: ColumnsType<AgentLedgerRow> = useMemo(
    () => [
      {
        key: 'agent',
        title: t('nav.agents'),
        render: (_, row) => (
          <Space>
            <Typography.Text strong>{row.agent.displayName}</Typography.Text>
            <Typography.Text type="secondary">{row.agent.code}</Typography.Text>
          </Space>
        ),
      },
      {
        key: 'settlement',
        title: t('agents.settlementMode'),
        width: 140,
        render: (_, row) => t(`settlementMode.${row.agent.settlementMode}`),
      },
      {
        key: 'balance',
        title: t('common.balance'),
        width: 160,
        align: 'right',
        render: (_, row) =>
          row.loading ? (
            '…'
          ) : (
            <Typography.Text type={(row.balance?.amount ?? 0) < 0 ? 'danger' : undefined}>
              {formatMoneyValue(row.balance)}
            </Typography.Text>
          ),
      },
      {
        key: 'available',
        title: t('agents.available'),
        width: 160,
        align: 'right',
        render: (_, row) => (row.loading ? '…' : formatMoneyValue(row.available)),
      },
      {
        key: 'debtLimit',
        title: t('agents.debtLimit'),
        width: 160,
        align: 'right',
        render: (_, row) =>
          row.loading
            ? '…'
            : row.debtLimit === null
              ? t('agents.debtLimitNotSet')
              : formatMoneyValue(row.debtLimit),
      },
    ],
    [t],
  )

  return (
    <>
      <PageHeader title={t('nav.ledger')} description={t('ledger.intro')} />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} xl={15}>
          <Card title={t('ledger.accounts.title')}>
            <DataTable<LedgerAccount>
              queryKey={queryKeys.ledger.accounts}
              fetchPage={(state, signal) =>
                fetchLedgerAccounts(toAccountsFilter(state), state.page, state.pageSize, signal)
              }
              columns={accountColumns}
              rowKey={(row) => row.id}
              tableId="ledger-accounts"
              stickyFirstColumn
              scrollX={1132}
              // ⚠️ Jami son SERVER BERMAYDI — javobda faqat `hasNext`.
              // «1–25 / N» yozuvi taxminni haqiqat qilib ko'rsatardi.
              showTotal={false}
              toolbarExtra={<AccountOwnerFilter />}
              emptyTitle={t('ledger.accounts.empty')}
              emptyDescription={t('ledger.accounts.emptyHint')}
              onRow={(row) => ({
                style: { cursor: 'pointer' },
                onClick: () => setOpenAccount(row),
              })}
            />
          </Card>
        </Col>

        <Col xs={24} xl={9}>
          <Card title={t('ledger.reconciliation.title')}>
            {reconciliation.isError ? (
              <NotReadyAlert error={reconciliation.error} hint={t('ledger.reconciliation.notReadyHint')} />
            ) : reconciliation.data ? (
              <>
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label={t('reconcile.checkedAt')}>
                    {formatDateTime(reconciliation.data.checkedAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('ledger.reconciliation.accountsChecked')}>
                    {reconciliation.data.accountsChecked}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('ledger.reconciliation.mismatches')}>
                    <Tag color={reconciliation.data.clean ? 'green' : 'red'}>
                      {reconciliation.data.findings.length}
                    </Tag>
                  </Descriptions.Item>
                </Descriptions>
                {reconciliation.data.findings.length > 0 ? (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginTop: 12 }}
                    message={t('ledger.reconciliation.mismatchTitle')}
                    description={
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {reconciliation.data.findings.slice(0, 10).map((f) => (
                          <ReconciliationFinding key={`${f.check}-${f.subject}`} finding={f} />
                        ))}
                      </Space>
                    }
                  />
                ) : null}
              </>
            ) : (
              <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
            )}
          </Card>
        </Col>
      </Row>

      <Card title={t('ledger.agentBalances')}>
        {/*
          ⚠️ SAHIFALASH YO'Q — `GET /api/agents/roots` uni QO'LLAMAYDI
          (`page`/`size` parametri yo'q) va har agent balansi ALOHIDA
          so'rov bilan keladi. Soxta sahifalash yasash o'rniga bo'lim
          o'z holicha qoldirildi; ro'yxat ildiz agentlar bilan cheklangan
          va u odatda kichik.
        */}
        {/*
          ⚠️ Xato — «agent yo'q» dan AJRATILGAN. Pul ekranida bu farq eng
          qimmat: bo'sh balans jadvali «qarzdorlik yo'q» degan xulosaga
          olib boradi, holbuki so'rov shunchaki yiqilgan bo'lishi mumkin.
        */}
        {roots.isError ? (
          <QueryErrorState error={roots.error} onRetry={() => void roots.refetch()} />
        ) : (
          <Table<AgentLedgerRow>
            columns={agentColumns}
            dataSource={agentRows}
            rowKey={(row) => row.agent.id}
            loading={roots.isPending}
            size="middle"
            pagination={false}
            scroll={{ x: 900 }}
            locale={{
              emptyText: (
                <EmptyState variant="no-data" title={t('ledger.noAgents')} description={t('ledger.noAgentsHint')} />
              ),
            }}
          />
        )}

        {/* Yashirilganlar YO'QOLMAYDI — nechtaligi aytiladi */}
        {hiddenAgents > 0 ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('ledger.closedAgentsHidden', { count: hiddenAgents })}
          </Typography.Text>
        ) : null}
      </Card>

      {/* Telefonda TO'LIQ EKRAN — `global.css` dagi umumiy qoida BARCHA
          Drawer/Modal uchun ishlaydi, bu yerda alohida sozlash shart emas */}
      <Drawer
        title={openAccount ? <LedgerOwnerName type={openAccount.ownerType} id={openAccount.ownerId} /> : ''}
        open={openAccount !== null}
        onClose={() => setOpenAccount(null)}
        width={720}
        styles={{ body: { padding: 16 } }}
        extra={
          openAccount && can('ledger.adjust') ? (
            <Button danger size="small" onClick={() => setAdjustOpen(true)}>
              {t('ledger.adjust.button')}
            </Button>
          ) : null
        }
      >
        {openAccount ? (
          <DataTable<LedgerEntry>
            // ⚠️ ALOHIDA URL PREFIKSI: bu sahifada ikkinchi jadval va
            // prefikssiz ikkalasi bir-birining sahifa raqamini o'chirib
            // yuborardi (`tableState.paramNames`).
            urlPrefix="entry"
            queryKey={[...queryKeys.ledger.entries(openAccount.id), entriesFrom, entriesTo]}
            fetchPage={(state, signal) =>
              fetchLedgerEntries(
                openAccount.id,
                entriesFrom,
                entriesTo,
                state.page,
                state.pageSize,
                signal,
              )
            }
            columns={entryColumns}
            rowKey={(row) => row.id}
            size="small"
            scrollX={700}
            // Jami son bu yerda ham YO'Q — `hasNext` dan boshqa signal yo'q
            showTotal={false}
            toolbarExtra={
              // Sana oralig'i MAJBURIY — `@RequestParam Instant from/to`
              // da sukut qiymati yo'q, hisob tarixi esa cheksiz o'sadi
              <RangePicker
                value={entriesRange}
                onChange={(value) => {
                  if (value?.[0] && value[1]) setEntriesRange([value[0], value[1]])
                }}
                allowClear={false}
                format="DD.MM.YYYY"
              />
            }
            emptyTitle={t('ledger.entries.empty')}
            emptyDescription={t('ledger.entries.emptyHint')}
          />
        ) : null}
      </Drawer>

      {openAccount ? (
        <AdjustAccountModal account={openAccount} open={adjustOpen} onClose={() => setAdjustOpen(false)} />
      ) : null}

      <LedgerTxnModal txnId={openTxnId} onClose={() => setOpenTxnId(null)} />
    </>
  )
}
